import { CategoryType, ProductType } from "@/types/Products";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(timezone);
dayjs.extend(isBetween);

export interface GroupedProduct {
  category: CategoryType;
  products: ProductType[];
}

export const isProductNew = (activationDate: string | undefined): boolean => {
  if (!activationDate) {
    return false;
  }

  if (isProductComingSoon(activationDate)) {
    return false;
  }

  const nowInMoscow = dayjs().tz("Europe/Moscow").startOf("day");
  const productActivationDate = dayjs(activationDate)
    .tz("Europe/Moscow")
    .startOf("day");
  const activationDatePlus7Days = productActivationDate.add(7, "days");

  return nowInMoscow.isBetween(
    productActivationDate,
    activationDatePlus7Days,
    null,
    "[]"
  );
};

export const isProductComingSoon = (activationDate: string): boolean => {
  const nowInMoscow = dayjs().tz("Europe/Moscow").startOf("day");
  const thirtyDaysLaterInMoscow = nowInMoscow.add(30, "days");

  const productActivationDate = dayjs(activationDate)
    .tz("Europe/Moscow")
    .startOf("day");

  return productActivationDate.isBetween(
    nowInMoscow,
    thirtyDaysLaterInMoscow,
    null,
    "[]"
  );
};

const isProductFree = (product: ProductType): boolean => {
  return !!product.free;
};

export const groupProductsByCategory = (
  products: ProductType[]
): GroupedProduct[] | undefined => {
  const categoryMap: Map<number, GroupedProduct> = new Map();

  // List of priority categories
  const priorityCategories = [
    "Медитации",
    "Настройки",
    "Лечебные сессии",
    "База Знаний",
    "Обучение",
  ];

  if (products.length > 0) {
    // Group products by category
    products.forEach((product) => {
      if (product.categories && product.categories.length > 0) {
        product.categories.forEach((category) => {
          if (!categoryMap.has(category.id!)) {
            categoryMap.set(category.id!, { category, products: [] });
          }
          categoryMap.get(category.id!)?.products.push(product);
        });
      }
    });

    // Sort categories by priority
    const groupedProducts = Array.from(categoryMap.values());
    groupedProducts.sort((a, b) => {
      const aCategoryName = a.category.title;
      const bCategoryName = b.category.title;

      const aPriority = priorityCategories.indexOf(aCategoryName!);
      const bPriority = priorityCategories.indexOf(bCategoryName!);

      if (aPriority !== -1 && bPriority === -1) {
        return -1;
      }
      if (aPriority === -1 && bPriority !== -1) {
        return 1;
      }
      return aPriority - bPriority;
    });

    // Sort products inside each category
    groupedProducts.forEach((group) => {
      group.products.sort((a, b) => {
        const statusA = getProductStatus(a);
        const statusB = getProductStatus(b);

        if (statusA === "locked" && statusB !== "locked") {
          return 1; // a goes to the end
        }
        if (statusB === "locked" && statusA !== "locked") {
          return -1; // b goes to the end
        }

        if (statusA === "comingSoon") {
          return 1; // a goes to the end
        }
        if (statusB === "comingSoon") {
          return -1; // b goes to the end
        }

        if (statusA === "new" && statusB !== "new") {
          return -1; // a is new, so it comes before b
        }
        if (statusB === "new" && statusA !== "new") {
          return 1; // b is new, so it comes after a
        }

        return 0; // Default if statuses are the same
      });
    });

    return groupedProducts;
  }

  return undefined;
};

export const getProductsByCategoryId = (
  categoryId: number,
  products: ProductType[]
): GroupedProduct | undefined => {
  // Filter out products that belong to the specified category
  const filteredProducts = products.filter((product) =>
    product.categories?.some((category) => category.id === categoryId)
  );

  if (filteredProducts.length === 0) {
    return undefined; // If no products belong to the given category, return undefined
  }

  // Sort products by the desired priority (new unlocked, unlocked, new locked, coming soon)
  filteredProducts.sort((a, b) => {
    const statusA = getProductStatus(a);
    const statusB = getProductStatus(b);

    if (statusA === "locked" && statusB !== "locked") {
      return 1; // a goes to the end
    }
    if (statusB === "locked" && statusA !== "locked") {
      return -1; // b goes to the end
    }

    if (statusA === "comingSoon") {
      return 1; // a goes to the end
    }
    if (statusB === "comingSoon") {
      return -1; // b goes to the end
    }

    if (statusA === "new" && statusB !== "new") {
      return -1; // a is new, so it comes before b
    }
    if (statusB === "new" && statusA !== "new") {
      return 1; // b is new, so it comes after a
    }

    return 0; // Default if statuses are the same
  });

  // Find the category object for the given categoryId from the first product's categories
  const category = filteredProducts[0].categories?.find(
    (category) => category.id === categoryId
  );

  // Return the category and the sorted products
  return {
    category: category!,
    products: filteredProducts,
  };
};

export const getComingSoonProducts = (
  products: ProductType[]
): ProductType[] => {
  const comingSoonProducts = products.filter(
    (product) =>
      product.activation_date && isProductComingSoon(product.activation_date)
  );

  comingSoonProducts.sort((a, b) => {
    const statusA = getProductStatus(a);
    const statusB = getProductStatus(b);

    // Step 1: Push locked products (new and locked) to the end of the list
    if (statusA === "locked" && statusB !== "locked") {
      return 1; // `a` goes to the end
    }
    if (statusB === "locked" && statusA !== "locked") {
      return -1; // `b` goes to the end
    }

    // Step 2: Sort new locked products to appear before other locked products
    if (statusA === "new" && statusB !== "new") {
      return -1; // `a` is new, so it comes before `b`
    }
    if (statusB === "new" && statusA !== "new") {
      return 1; // `b` is new, so it comes after `a`
    }

    // Step 3: Push coming soon products to the very last part, if needed
    if (statusA === "comingSoon" && statusB !== "comingSoon") {
      return 1;
    }
    if (statusB === "comingSoon" && statusA !== "comingSoon") {
      return -1;
    }

    // Step 4: Finally, sort by activation date for coming soon products
    return (
      dayjs(a.activation_date).diff(dayjs(), "day") -
      dayjs(b.activation_date).diff(dayjs(), "day")
    );
  });

  return comingSoonProducts;
};

export const getNewProducts = (products: ProductType[]): ProductType[] => {
  const newProducts = products.filter((product) =>
    isProductNew(product.activation_date)
  );

  newProducts.sort((a, b) => {
    const statusA = getProductStatus(a);
    const statusB = getProductStatus(b);

    // Step 1: Push locked products (new and locked) to the end of the list
    if (statusA === "locked" && statusB !== "locked") {
      return 1; // `a` goes to the end
    }
    if (statusB === "locked" && statusA !== "locked") {
      return -1; // `b` goes to the end
    }

    // Step 2: Sort new locked products to appear before other locked products
    if (statusA === "new" && statusB !== "new") {
      return -1; // `a` is new, so it comes before `b`
    }
    if (statusB === "new" && statusA !== "new") {
      return 1; // `b` is new, so it comes after `a`
    }

    // Step 3: Push coming soon products to the very last part
    if (statusA === "comingSoon" && statusB !== "comingSoon") {
      return 1;
    }
    if (statusB === "comingSoon" && statusA !== "comingSoon") {
      return -1;
    }

    // Step 4: Sort by activation date for new products
    return dayjs(a.activation_date).diff(dayjs(b.activation_date), "day");
  });

  return newProducts;
};

const getProductStatus = (
  product: ProductType
): "new" | "locked" | "comingSoon" | "free" => {
  if (
    isProductNew(product.activation_date) &&
    !(product.activation_date && isProductComingSoon(product.activation_date))
  ) {
    if (isProductFree(product)) {
      return "new";
    }
    return "locked";
  }

  if (product.activation_date && isProductComingSoon(product.activation_date)) {
    return "comingSoon";
  }

  if (
    isProductFree(product) &&
    !isProductNew(product.activation_date) &&
    !(product.activation_date && isProductComingSoon(product.activation_date))
  ) {
    return "free";
  }

  return "locked";
};
